// API calls for app API namespace - aspirationally
//
// Actually these functions were ported from the legacy mixins module so
// have a couple quirks:
//   - In many cases `this` is assumed to be bound to a Vue component
//   - There is some admin stuff lumped in here
// TODO: Get rid of the admin stuff, all the `this`.
//       Each function should just return the response data

import { excludeEmptyValues } from '@lib/collections'
import axiosBase from '@lib/apiClient'

// @TODO These Pinia imports are supporting the global page loading behavior
// that happens when the app pages load. The unfortunate side effect of this
// is that it means this entire module is unusable outside of a Pinia context.
// This coupling isn't ideal because we want these exported methods to be pure
// functions that DON'T cause side-effeccts. The tentative plan is to refactor
// this such that the Pinia dependency only happens where it's relevant. There
// is a ticket in Linear for this.
import { useLoadingStore } from '@app/stores/LoadingStore'

const surveyShowUrl = (survey_id) => `/app/api/surveys/${survey_id}.json`

// ** Axios Clients **
// Each axios client has a different loading indicator behavior:
// - rawAxios: no indicator
// - spinnerAxios: A loading spinner
// - toastAxios: A customizable success or error message via toast popup
// ** ------------- **
const rawAxios = axiosBase.create()
const spinnerAxios = axiosBase.create()
// You can pass a custom successMessage in the request config like so:
//
// return toastAxios.post(
//   `/app/api/v2/case_studies/${id}/sections.json`,
//   { type, section },
//   { successMessage: 'foobar' },
// )
const toastAxios = axiosBase.create()

// Enter loading state when any request is initiated
spinnerAxios.interceptors.request.use((config) => {
  useLoadingStore().trackRequest(config)
  return config
})

function handleResponseComplete(config) {
  useLoadingStore().untrackRequest(config.config)
  return config
}
// Exit loading state when any response is received
// `use` takes two arguments: a function to call on completed response and one to
// call on error. That's why the argument is repeated twice, we want to do the same thing
// in both cases which is to turn off the loading indicator.
spinnerAxios.interceptors.response.use(handleResponseComplete, handleResponseComplete)

toastAxios.interceptors.response.use(
  (response) => {
    // TODO: figure out how to handle, BF
    // Vue does not natively have a toast method, so im commenting this out for now.

    // const successMessage = response.config.successMessage || 'Saved successfully! ✅'
    // Vue.toast(successMessage)
    return response
  },
  function (_error) {
    // Vue.toast('An error occured')
  },
)

export function getSurvey(survey_id) {
  return spinnerAxios.get(surveyShowUrl(survey_id))
}

export function fetchApp() {
  return spinnerAxios.get('/app/api/app.json')
}

export function createSurvey(survey) {
  return spinnerAxios.post('/app/api/surveys.json', { survey })
}

export function updateSurvey(survey) {
  return spinnerAxios.patch(`/app/api/surveys/${survey.id}.json`, { survey })
}

export function deleteSurvey(surveyId) {
  return spinnerAxios.delete(`/app/api/surveys/${surveyId}.json`)
}

export function cloneSurvey(surveyId) {
  return spinnerAxios.post(`/app/api/surveys/${surveyId}/copy.json`)
}

export function getSurveyQuestions(survey_id) {
  return spinnerAxios.get(`/app/api/surveys/${survey_id}/questions.json`)
}

export function getSurveys(params) {
  return spinnerAxios.get('/app/api/surveys.json', { params: params })
}

export function getSurveysWithResponses(params) {
  const paramsWithResponses = { with_responses: true, ...params }
  return getSurveys(paramsWithResponses)
}

export function getSurveyStats(surveyId) {
  return spinnerAxios.get(`/app/api/surveys/${surveyId}/stats.json`)
}

export function saveContentAsset(contentAsset) {
  return spinnerAxios.patch(`/app/api/content_assets/${contentAsset.id}.json`, {
    content_asset: { ...contentAsset },
  })
}

// TODO:
// this is there to support the store which uses the update method
// for calls to 'save'.  This should be refactored to only have one of these
export function updateContentAsset(contentAsset) {
  return saveContentAsset(contentAsset)
}
export function createContentAsset(contentAsset) {
  return spinnerAxios.post('/app/api/content_assets.json', { content_asset: contentAsset })
}

export function getTestimonialThemes(account_id, version) {
  let url = `/api/accounts/${account_id}/testimonial_themes.json`
  if (version) {
    url += `?version=${version}`
  }
  return spinnerAxios.get(url)
}

/*
 * This function fetches all testimonial themes for a given account_id and returns them as a map
 * with the theme id as the key.
 */
export async function getTestimonialThemesMap(account_id) {
  const response = await getTestimonialThemes(account_id)
  let fetchedThemes = {}
  response.data.testimonial_themes.map((t) => {
    fetchedThemes[t.id] = t
  })
  return fetchedThemes
}

export function getTestimonialTheme(id) {
  return spinnerAxios.get(`/api/testimonial_themes/${id}.json`)
}

export function postTestimonialTheme(theme) {
  return spinnerAxios.post('/app/api/testimonial_themes.json', theme)
}

export function patchTestimonialTheme(id, theme) {
  return spinnerAxios.patch(`/app/api/testimonial_themes/${id}.json`, theme)
}

export function deleteTestimonialTheme(id) {
  return spinnerAxios.delete(`/app/api/testimonial_themes/${id}.json`)
}

export function getTestimonialThemeFontFamilies(testimonialThemeId) {
  return spinnerAxios.get(`/api/testimonial_themes/${testimonialThemeId}/font_families.json`)
}

export function getTestimonialThemeFontFamily(testimonialThemeId, fontFamilyId) {
  return spinnerAxios.get(
    `/api/testimonial_themes/${testimonialThemeId}/font_families/${fontFamilyId}.json`,
  )
}

export function postTestimonialThemeFontFamily(testimonialThemeId, fontFamily) {
  let params = { font_family: { ...fontFamily } }

  return spinnerAxios.post(
    `/app/api/testimonial_themes/${testimonialThemeId}/font_families.json`,
    params,
  )
}

export function patchTestimonialThemeFontFamily(testimonialThemeId, fontFamilyId, fontFamily) {
  let params = { font_family: { ...fontFamily } }

  return spinnerAxios.patch(
    `/app/api/testimonial_themes/${testimonialThemeId}/font_families/${fontFamilyId}.json`,
    params,
  )
}

export function deleteTestimonialThemeFontFamily(testimonialThemeId, fontFamilyId) {
  return spinnerAxios.delete(
    `/app/api/testimonial_themes/${testimonialThemeId}/font_families/${fontFamilyId}.json`,
  )
}

export function getTestimonialThemeFontFaces(testimonialThemeId, fontFamilyId) {
  return spinnerAxios.get(
    `/api/testimonial_themes/${testimonialThemeId}/font_families/${fontFamilyId}/font_faces.json`,
  )
}

export function getTestimonialThemeFontFace(testimonialThemeId, fontFamilyId, fontFaceId) {
  return spinnerAxios.get(
    `/api/testimonial_themes/${testimonialThemeId}/font_families/${fontFamilyId}/font_faces/${fontFaceId}.json`,
  )
}

export function postTestimonialThemeFontFace(testimonialThemeId, fontFamilyId, fontFace) {
  const params = new FormData()

  params.append('font_face[style]', fontFace.style || '')
  params.append('font_face[weight]', fontFace.weight || '')
  if (fontFace.font_file) {
    params.append('font_face[font_file]', fontFace.font_file)
  }

  return spinnerAxios.post(
    `/app/api/testimonial_themes/${testimonialThemeId}/font_families/${fontFamilyId}/font_faces.json`,
    params,
  )
}

export function patchTestimonialThemeFontFace(
  testimonialThemeId,
  fontFamilyId,
  fontFaceId,
  fontFace,
) {
  const params = new FormData()

  params.append('font_face[style]', fontFace.style || '')
  params.append('font_face[weight]', fontFace.weight || '')
  if (fontFace.font_file) {
    params.append('font_face[font_file]', fontFace.font_file)
  }

  return spinnerAxios.patch(
    `/app/api/testimonial_themes/${testimonialThemeId}/font_families/${fontFamilyId}/font_faces/${fontFaceId}.json`,
    params,
  )
}

export function deleteTestimonialThemeFontFace(testimonialThemeId, fontFamilyId, fontFaceId) {
  return spinnerAxios.delete(
    `/app/api/testimonial_themes/${testimonialThemeId}/font_families/${fontFamilyId}/font_faces/${fontFaceId}.json`,
  )
}

export function getContentAsset(content_asset_id) {
  return spinnerAxios.get(`/app/api/content_assets/${content_asset_id}.json`)
}

export function recalculateContentAsset(contentAsset) {
  return spinnerAxios.get(`/app/api/content_assets/${contentAsset.id}/recalculate.json`, {
    params: {
      filter: contentAsset.filter,
      filter_data: contentAsset.filter_data,
      answer_id: contentAsset.answer_id,
      display_order: contentAsset.display_order,
    },
  })
}

export function recalculateMatrixxChart(matrixxChart) {
  return spinnerAxios.get(`/app/api/matrixx_charts/${matrixxChart.id}/recalculate.json`, {
    params: {
      filter: matrixxChart.filter,
      filter_data: matrixxChart.filter_data,
    },
  })
}

export function saveAssetList(id, assetList) {
  return spinnerAxios.patch(`/app/api/asset_lists/${id}.json`, { asset_list: assetList })
}

export function getAssetList(asset_list_id) {
  return spinnerAxios.get(`/app/api/asset_lists/${asset_list_id}.json`)
}

export function reorderAssetListItem(asset_list_item_id, position) {
  return spinnerAxios.post(`/app/api/asset_list_items/${asset_list_item_id}/reorder.json`, {
    position: position,
  })
}

export function createTestimonial(renderable_testimonial, params = {}) {
  // This #create action wants a list of classifier_value_ids but usually we'll have a
  // list of classifier_values, so we'll munge the data a bit.
  const classifier_value_ids = renderable_testimonial.classifier_values?.map((c) => c.id)
  const survey_response_id = renderable_testimonial?.survey_response?.id

  return spinnerAxios.postForm('/app/api/renderable_testimonials.json', {
    renderable_testimonial: {
      classifier_value_ids: JSON.stringify(classifier_value_ids),
      survey_response_id,
      ...renderable_testimonial,
    },
    // Set aditional params that are not part of the renderable testimonial object
    ...params,
  })
}

export function getCustomerSpotlight(customer_spotlight_id) {
  return spinnerAxios.get(`/app/api/customer_spotlights/${customer_spotlight_id}.json`)
}

export function createCustomerSpotlight(customer_spotlight) {
  return spinnerAxios.post('/app/api/customer_spotlights.json', {
    customer_spotlight: customer_spotlight,
  })
}

export function updateCustomerSpotlight(customer_spotlight) {
  return spinnerAxios.patch(`/app/api/customer_spotlights/${customer_spotlight.id}.json`, {
    customer_spotlight: customer_spotlight,
  })
}

export function destroyCustomerSpotlight(customer_spotlight_id) {
  return spinnerAxios.delete(`/app/api/customer_spotlights/${customer_spotlight_id}.json`)
}

export function renderAsset(asset_id, options) {
  return spinnerAxios.post(`/api/renderable_testimonials/${asset_id}/download.json`, options)
}

export function downloadTestimonialPptx(id) {
  return spinnerAxios.get(`/api/renderable_testimonials/${id}/download_pptx.json`, {
    responseType: 'blob',
  })
}

export function renderCaseStudy(id, options) {
  return spinnerAxios.post(`/api/case_studies/${id}/download.json`, options)
}

export function renderCustomerSpotlight(id, options) {
  return spinnerAxios.post(`/api/customer_spotlights/${id}/download.json`, options)
}

export function renderSurveySpotlight(identifier, options) {
  return spinnerAxios.post(`/api/content_assets/${identifier}/renderings/download.json`, options)
}

export function downloadMatrixxChart(id, options) {
  return spinnerAxios.post(`/api/matrixx_charts/${id}/download.json`, options)
}

export function downloadContentAsset(id, options) {
  return spinnerAxios.post(`/api/content_assets/${id}/download.json`, options)
}

export function getRenderableTestimonial(id_or_hash) {
  return spinnerAxios.get(`/api/renderable_testimonials/${id_or_hash}.json`)
}

export function updateRenderableTestimonial(renderableTestimonial) {
  const classifier_value_ids = renderableTestimonial.classifier_values?.map((c) => c.id)

  return spinnerAxios.patchForm(
    `/app/api/renderable_testimonials/${renderableTestimonial.id}.json`,
    {
      renderable_testimonial: {
        ...renderableTestimonial,
        classifier_value_ids: JSON.stringify(classifier_value_ids),
      },
    },
  )
}

// Fetches a matrixx chart instance from the App Api
export function getMatrixxChart(id) {
  return spinnerAxios.get(`/app/api/matrixx_charts/${id}.json`)
}

// Fetches a matrixx chart instance from the public api
export function fetchPublicMatrixxChart(id) {
  return spinnerAxios.get(`/api/matrixx_charts/${id}.json`)
}

export function updateMatrixxChart(matrixx_chart) {
  return spinnerAxios.patch(`/app/api/matrixx_charts/${matrixx_chart.id}.json`, {
    matrixx_chart: matrixx_chart,
  })
}

export function createMatrixxChart(matrixx_chart) {
  return spinnerAxios.post(`/app/api/matrixx_charts.json`, { matrixx_chart: matrixx_chart })
}

export function deleteMatrixxChart(matrixx_chart) {
  return spinnerAxios.delete(`/app/api/matrixx_charts/${matrixx_chart.id}.json`)
}

export function fetchPublicContentAsset(content_asset_id, options) {
  return spinnerAxios.get(`/api/content_assets/${content_asset_id}.json`, { params: options })
}

export function patchRenderableTestimonial(rt) {
  return spinnerAxios.patch(`/app/api/renderable_testimonials/${rt.id}.json`, {
    renderable_testimonial: rt,
  })
}

export function deleteRenderableTestimonial(id) {
  return spinnerAxios.delete(`/app/api/renderable_testimonials/${id}.json`)
}

export function postImportTestimonials(testimonial_data) {
  return spinnerAxios.post('/app/api/renderable_testimonials/import.json', {
    testimonials: testimonial_data,
  })
}

export function createRecipientUpload(postData) {
  return spinnerAxios.post('/app/api/recipient_uploads.json', postData)
}

export function getSurveyRecipientUploads(survey_id) {
  return spinnerAxios.get(`/app/api/surveys/${survey_id}/recipient_uploads.json`)
}

export function getSurveyResponses(params) {
  return spinnerAxios.get(`/app/api/survey_responses.json`, { params: params })
}

export function getSurveyResponse(id) {
  return spinnerAxios.get(`/app/api/survey_responses/${id}.json`)
}

export function updateSurveyResponse(survey_response) {
  return rawAxios.patch(`/app/api/survey_responses/${survey_response.id}.json`, {
    survey_response,
  })
}

export function getSurveyResponsesByQuestion(surveyId, questionId, params) {
  return spinnerAxios.get(
    `/app/api/surveys/${surveyId}/survey_responses.json?question_id=${questionId}`,
    { params: params },
  )
}

export function getRecipientUpload(recipient_upload_id) {
  return spinnerAxios.get(`/app/api/recipient_uploads/${recipient_upload_id}.json`)
}

export function getRecipientUploadToken(survey_id) {
  return spinnerAxios.get(`/app/api/tokens/recipients_upload/${survey_id}`)
}

export function getFilteredRecipients(url) {
  return spinnerAxios.get(url)
}

export function postSendRequest(send_request) {
  return spinnerAxios.post('/app/api/send_requests.json', {
    survey_id: send_request.survey_id,
    send_request: send_request,
  })
}

export function postBulkSendRequest(survey_id, emails) {
  return spinnerAxios.post(`/app/api/send_requests/bulk_create.json`, {
    survey_id: survey_id,
    send_requests: emails,
  })
}

export function patchSendRequest(send_request) {
  return spinnerAxios.patch(`/app/api/send_requests/${send_request.id}.json`, {
    send_request: send_request,
  })
}

export function postPreviewSendRequest(survey_id, preview_email_string, email) {
  return spinnerAxios.post(`/app/api/send_requests/send_preview.json`, {
    survey_id: survey_id,
    preview_email_string: preview_email_string,
    subject: email.subject,
    email_text: email.email_text,
  })
}

export function getUsers() {
  return spinnerAxios.get(`/app/api/users.json`)
}

// Fetches an individual user from within the logged in user's account
// Only editors can make this call
export function getUser(userId) {
  return spinnerAxios.get(`/app/api/users/${userId}.json`)
}

// Updates an individual user from within the logged in user's account
// Only editors can make this call
export function updateAccountUser(user) {
  return spinnerAxios.patch(`/app/api/account_users/${user.id}.json`, { user: user })
}

export function postUser(user) {
  return spinnerAxios.post(`/app/api/users.json`, { user: user })
}

// Our API currently had two endpoints for fetching recipients. One is scoped to a survey
// and the other is not. This function hides that detail from the frontend by choosing the
// correct endpoint based on the presence of a surveyId.
export function getRecipients(surveyId, params) {
  if (surveyId) {
    return spinnerAxios.get(`/app/api/surveys/${surveyId}/recipients.json`, { params: params })
  } else {
    return getAllRecipients(params)
  }
}

export function getRecipient(id) {
  return spinnerAxios.get(`/app/api/recipients/${id}.json`)
}

export function getAllRecipients(params) {
  return spinnerAxios.get(`/app/api/recipients.json`, { params: excludeEmptyValues(params) })
}

export function getSurveyReports(surveyId, params) {
  return spinnerAxios.get(`/app/api/surveys/${surveyId}/reports.json`, { params: params })
}

export function getRecipientRoles() {
  return spinnerAxios.get(`/api/recipient_roles.json`)
}

export function postRecipients(url, method, recipient) {
  return spinnerAxios({ url: url, method: method, data: { recipient: recipient } })
}

export function deleteSubscription(recipient_id) {
  return spinnerAxios.delete(`/api/email_subscriptions/${recipient_id}.json`)
}

export function postResponse(response) {
  return spinnerAxios.post('/api/responses.json', response)
}

export function patchResponse(response) {
  const url = `/api/responses/${response.id}.json`
  return spinnerAxios.patch(url, response)
}

export function deleteResponse(response) {
  return spinnerAxios.delete(`/api/responses/${response.id}.json`)
}

export function getTags(taggable_id, taggable_type) {
  return rawAxios.get(
    `/app/api/tags.json?taggable_id=${taggable_id}&taggable_type=${taggable_type}`,
  )
}

export function updateTags(taggable_id, taggable_type, tags) {
  return spinnerAxios.patch(`/app/api/tags/general_update.json`, {
    taggable_id,
    taggable_type,
    tags,
  })
}

/*
 * Questions and Answers functions
 */
export function getQuestion(questionId, surveyId) {
  return spinnerAxios.get(`/app/api/v2/surveys/${surveyId}/questions/${questionId}.json`)
}

export function createQuestion(question) {
  return spinnerAxios.post(`/app/api/questions.json`, { question: question })
}

export function deleteQuestion(question) {
  return spinnerAxios.delete(`/app/api/questions/${question.id}.json`)
}

export function updateQuestion(question) {
  return spinnerAxios.patch(`/app/api/questions/${question.id}.json`, { question })
}

export function excludeStatTemplate(surveyId, questionId, statTemplateId) {
  return spinnerAxios.post(
    `/app/api/v2/surveys/${surveyId}/questions/${questionId}/exclude_stat_template.json`,
    {
      stat_template_id: statTemplateId,
    },
  )
}

// Gets a question with all the answer stats associated with it
export function getQuestionWithStats(question_id) {
  return spinnerAxios.get(`/app/api/questions/${question_id}/with_stats.json`)
}

export function createAnswer(answer) {
  return spinnerAxios.post(`/app/api/answers.json`, answer)
}

export function deleteAnswer(answer) {
  return spinnerAxios.delete(`/app/api/answers/${answer.id}.json`)
}

export function updateAnswer(answer) {
  return spinnerAxios.patch(`/app/api/answers/${answer.id}.json`, {
    answer: { the_answer: answer.the_answer },
  })
}

/* Question Rules APIs */

/**
 * Creates a new question rule group for a given question.
 * @param {number} questionId - The ID of the question.
 * @param {QuestionRuleGroupPayload} questionRuleGroup - The question rule group data.
 * @returns {Promise<QuestionRuleGroup>} A promise that resolves to the created question rule group.
 */
export function createQuestionRuleGroup(questionId, questionRuleGroup) {
  return spinnerAxios.post(`/app/api/questions/${questionId}/question_rule_groups.json`, {
    question_rule_group: questionRuleGroup,
  })
}

/**
 * Updates an existing question rule group for a given question.
 * @param {number} questionId - The ID of the question.
 * @param {number} questionRuleGroupId - The ID of the question rule group.
 * @param {QuestionRuleGroupPayload} questionRuleGroup - The updated question rule group data.
 * @returns {Promise<QuestionRuleGroup>} A promise that resolves to the updated question rule group.
 */
export function updateQuestionRuleGroup(questionId, questionRuleGroupId, questionRuleGroup) {
  return spinnerAxios.patch(
    `/app/api/questions/${questionId}/question_rule_groups/${questionRuleGroupId}.json`,
    { question_rule_group: questionRuleGroup },
  )
}

/**
 * Deletes an existing question rule group for a given question.
 * @param {number} questionId - The ID of the question.
 * @param {number} questionRuleGroupId - The ID of the question rule group.
 * @returns {Promise<void>} A promise that resolves when the question rule group is deleted.
 */
export function deleteQuestionRuleGroup(questionId, questionRuleGroupId) {
  return spinnerAxios.delete(
    `/app/api/questions/${questionId}/question_rule_groups/${questionRuleGroupId}.json`,
  )
}

export function createClassifier(classifier) {
  return spinnerAxios.post(`/app/api/classifiers.json`, { classifier: classifier })
}

export function getClassifiers(params = {}) {
  return spinnerAxios.get('/app/api/classifiers.json', { params })
}

export function getClassifier(id) {
  return spinnerAxios.get(`/app/api/classifiers/${id}.json`)
}

export function updateClassifier(classifier) {
  return spinnerAxios.patch(`/app/api/classifiers/${classifier.id}.json`, { classifier })
}

export function deleteClassifier(classifier) {
  return spinnerAxios.delete(`/app/api/classifiers/${classifier.id}.json`)
}

export function createClassifierValue(classifier_value) {
  return spinnerAxios.post(
    `/app/api/classifiers/${classifier_value.classifier_id}/classifier_values.json`,
    { classifier_value: classifier_value },
  )
}

export function createClassification(asset_identifier, classifier_value_id) {
  return spinnerAxios.post('/app/api/classifications/', {
    classification: { asset_identifier, classifier_value_id },
  })
}

export function deleteClassification(asset_identifier, classifier_value_id) {
  // It's sketchy for a DELETE to have a body, but in this context it's very convenient since the relevant frontend
  // components tend not to have access to classification_id, so being a bit sketch for convenience
  return spinnerAxios.delete('/app/api/classifications/', {
    data: { classification: { asset_identifier, classifier_value_id } },
  })
}

export function updateClassifierValue(classifier_value) {
  return spinnerAxios.put(
    `/app/api/classifiers/${classifier_value.classifier_id}/classifier_values/${classifier_value.id}.json`,
    { classifier_value: classifier_value },
  )
}

export function deleteClassifierValue(classifier_value) {
  return spinnerAxios.delete(
    `/app/api/classifiers/${classifier_value.classifier_id}/classifier_values/${classifier_value.id}.json`,
  )
}

export function getIndustries() {
  return spinnerAxios.get('/api/industries.json')
}

// Fetches all the aggregated questions for a survey along with their stat templates and
// other meta info to power the aggregated results screen
// this endpoint filters out Testimonial questions.
export function fetchAggregate(survey_id) {
  return spinnerAxios.get(`/app/api/surveys/${survey_id}/aggregate.json`)
}

// Essentially the same as fetchAggregate but it does not filter out Testimonial questions.
export function fetchAggregatedQuestions(survey_id) {
  return spinnerAxios.get(`/app/api/surveys/${survey_id}/aggregated_questions.json`)
}

export function getQuestions(surveyId, best) {
  const params = best ? { best: true } : {}
  return spinnerAxios.get(`/app/api/v2/surveys/${surveyId}/questions.json`, { params: params })
}

// Fetches all the classifier_values associated with a recipient
export function fetchRecipientClassifierValues(recipient_id) {
  return spinnerAxios.get(`/app/api/recipients/${recipient_id}/classifier_values.json`)
}

export function updateAccount(account) {
  return spinnerAxios.put(`/app/api/v2/accounts/${account.id}.json`, { account })
}

export function attachAvatarToRecipient(recipient_id, avatar) {
  return spinnerAxios.putForm(`/app/api/recipients/${recipient_id}/avatar.json`, { avatar })
}

export function deleteAvatarFromRecipient(recipient_id) {
  return spinnerAxios.delete(`/app/api/recipients/${recipient_id}/avatar.json`)
}

export function fetchSurveySummaries(surveyId) {
  return spinnerAxios.get(`/app/api/v2/surveys/${surveyId}/survey_summaries.json`)
}

export function getSurveySummary(id) {
  return spinnerAxios.get(`/app/api/v2/survey_summaries/${id}.json`)
}

export function newSurveySummary(survey_id) {
  return spinnerAxios.get(`/app/api/v2/surveys/${survey_id}/survey_summaries/new.json`)
}

export function createSurveySummary(survey_id, survey_summary) {
  return spinnerAxios.post(`/app/api/v2/surveys/${survey_id}/survey_summaries.json`, survey_summary)
}

// In the near future we probably want to be able to initialize testimonials based on something other than responses.
// We can accomplish that by adding a type param that supports values like 'response' to specify what type of entity
// the id is for
export function newRenderableTestimonial(params) {
  return spinnerAxios.get(`/app/api/v2/testimonials/new.json`, { params: params })
}

export function exportTestimonialsCSV(params) {
  return spinnerAxios.post(`/app/api/v2/testimonials/export_csv.json`, { params: params })
}

// // // // // // // // //
//
// BRAND FONT ENDPOINTS //
//
// // // // // // // // //
export function createBrandFontFamily(family, accountId) {
  const payload = { brand_font: family }
  if (accountId) {
    payload.account_id = accountId
  }
  return spinnerAxios.post('/app/api/brand_font.json', payload)
}

export function createBrandFontFace(face, accountId) {
  const formData = new FormData()
  formData.append('style', face.style)
  formData.append('weight', face.weight)
  formData.append('font_file', face.font_file)
  if (accountId) {
    formData.append('account_id', accountId)
  }
  return spinnerAxios.post('/app/api/brand_font/font_faces.json', formData)
}

export function deleteBrandFontFace(faceId, accountId) {
  const payload = accountId ? { account_id: accountId } : {}
  return spinnerAxios.delete(`/app/api/brand_font/font_faces/${faceId}.json`, { data: payload })
}

// SlackBot
export function deleteSlackbotConnection() {
  return spinnerAxios.delete('/app/api/account/slackbot_connection.json')
}

// Gong methods...
export function getGongCall(id) {
  return spinnerAxios.get(`/app/api/v2/gong_calls/${id}.json`)
}

export function getGongCalls(params) {
  return spinnerAxios.get('/app/api/v2/gong_calls.json', { params: params })
}

export function createTranscriptAnalysisJob(params) {
  return spinnerAxios.post('/app/api/jobs/transcript_analysis.json', { ...params })
}

export function createVideoLinkAsset(asset) {
  const classifier_value_ids = asset.classifier_values?.map((c) => c.id)
  return spinnerAxios.post('/app/api/v2/video_link_assets.json', {
    video_link_asset: { ...asset, classifier_value_ids },
  })
}

export function updateVideoLinkAsset(asset) {
  const classifier_value_ids = asset.classifier_values?.map((c) => c.id)
  return spinnerAxios.patch(`/app/api/v2/video_link_assets/${asset.id}.json`, {
    video_link_asset: { ...asset, classifier_value_ids },
  })
}

export function getVideoLinkAsset(assetId) {
  return spinnerAxios.get(`/app/api/v2/video_link_assets/${assetId}.json`)
}

export function deleteVideoLinkAsset(asset) {
  return spinnerAxios.delete(`/app/api/v2/video_link_assets/${asset.id}.json`)
}

// File Assets
export function getFileAsset(id, params) {
  return spinnerAxios.get(`/app/api/v2/file_assets/${id}.json`, { ...params })
}

export function updateFileAsset(params) {
  const classifier_value_ids = params.classifier_values?.map((c) => c.id)
  return spinnerAxios.patch(`/app/api/v2/file_assets/${params['id']}.json`, {
    ...params,
    classifier_value_ids,
  })
}

export function downloadFileAsset(id) {
  return spinnerAxios.get(`/api/file_assets/${id}/download.json`)
}

export function deleteFileAsset(asset) {
  return spinnerAxios.delete(`/app/api/v2/file_assets/${asset.id}.json`)
}

// UERC Requests
export function createUercRequest(uerc_request) {
  return spinnerAxios.post('/app/api/v2/uerc_requests.json', uerc_request)
}

export function fetchCustomerSpotlightSummary(type, respondent_company, responses) {
  return rawAxios.post('/app/api/airops/summary', { type, respondent_company, responses })
}

export function importRecipients(surveyId, upload_id, recipients) {
  return spinnerAxios.post(`/app/api/surveys/${surveyId}/recipients/import.json`, {
    recipient_upload_id: upload_id,
    recipients,
  })
}

export function createRecipientImport(surveyId, recipientImport) {
  return spinnerAxios.post(`/app/api/surveys/${surveyId}/recipient_imports.json`, {
    ...recipientImport,
  })
}

export function createTestimonialImport(testimonialImport) {
  return spinnerAxios.post('/app/api/testimonial_imports.json', testimonialImport)
}

export function removeCaseStudyHeaderImage(caseStudyId) {
  return toastAxios.delete(`/app/api/v2/case_studies/${caseStudyId}/remove_header_image.json`)
}

export function removeCaseStudyCustomerLogo(caseStudyId) {
  return toastAxios.delete(`/app/api/v2/case_studies/${caseStudyId}/remove_customer_logo.json`)
}

export function createCaseStudy(asset) {
  const classifier_value_ids = asset.classifier_values?.map((c) => c.id)
  return spinnerAxios.post('/app/api/v2/case_studies.json', {
    case_study: { ...asset, classifier_value_ids },
  })
}

export function updateCaseStudy(asset) {
  const classifier_value_ids = asset.classifier_values?.map((c) => c.id)
  return toastAxios.patch(`/app/api/v2/case_studies/${asset.id}.json`, {
    case_study: { ...asset, classifier_value_ids },
  })
}

export function getCaseStudy(assetId) {
  return rawAxios.get(`/app/api/v2/case_studies/${assetId}.json`)
}

export function deleteCaseStudy(asset) {
  return spinnerAxios.delete(`/app/api/v2/case_studies/${asset.id}.json`)
}

export function createCaseStudySection(id, type, section) {
  return toastAxios.post(
    `/app/api/v2/case_studies/${id}/sections.json`,
    { type, section },
    { successMessage: 'Section added' },
  )
}

export function deleteCaseStudySection(id, index) {
  return toastAxios.delete(`/app/api/v2/case_studies/${id}/sections/${index}.json`, {
    successMessage: 'Section removed',
  })
}

export function moveCaseStudySection(id, index, forward) {
  return toastAxios.post(
    `/app/api/v2/case_studies/${id}/sections/${index}/move.json`,
    { forward },
    { successMessage: 'Section moved! ✅' },
  )
}

export function updateCaseStudySection(id, index, section) {
  return toastAxios.put(
    `/app/api/v2/case_studies/${id}/sections/${index}.json`,
    { section },
    { successMessage: 'Section saved! ✅' },
  )
}

export function getTestimonialsForRecipient(recipientId, caseStudyId) {
  return rawAxios.get(
    `/app/api/v2/testimonials/for_recipient.json?recipient_id=${recipientId}&case_study_id=${caseStudyId}`,
  )
}

export function getSurveyMetrics(surveyId) {
  return spinnerAxios.get(`/app/api/surveys/${surveyId}/metrics.json`)
}

export function getPeople(params) {
  return spinnerAxios.get(`/app/api/v2/people.json`, { params: excludeEmptyValues(params) })
}

export function getPerson(id) {
  return spinnerAxios.get(`/app/api/v2/people/${id}.json`)
}

export function getPersonAssets(id, params) {
  return spinnerAxios.get(`/app/api/v2/people/${id}/assets.json`, {
    params: excludeEmptyValues(params),
  })
}

export function getPersonActivities(id, params) {
  return spinnerAxios.get(`/app/api/v2/people/${id}/activities.json`, {
    params: excludeEmptyValues(params),
  })
}

/**
 * Exports people data by sending a POST request to the export endpoint.
 *
 * @param {Object} params - The parameters to include in the export request.
 */
export function exportAdvocates(params) {
  return rawAxios.post(`/app/api/v2/people/export_advocates.json`, params)
}
